<template>
  <div :ref="el => { if (el) handleRootMount(el as HTMLDivElement) }" class="listview"
    :class="[{ zoomout: zoomOutView }, view, 'group' + groupView]">
    <transition-group name="zoomfade">
      <template v-if="_list && observer" key="list">
        <div class="item-wrapper" v-for="(item, index) in _list" :style="[
          {
            backgroundColor: visibilityReference[keyGetter(item, index)] ? 'transparent' : printActive ? 'transparent' : '#cfcfcf',
          },
          _wrapperStyles,
        ]" :id="keyGetter(item, index)" :key="keyGetter(item, index)" :data-item-key="keyGetter(item, index)"
          :ref="el => { if (el) observer?.observe(el as HTMLDivElement) }">
          <component :is="printTemplate || itemTemplate" class="item_c print-only" v-if="printActive" :item="item"
            :index="index" :options="options"></component>

          <component :is="itemTemplate" class="item_c screen" :item="item" :index="index" :key="keyGetter(item, index)"
            v-if="visibilityReference[keyGetter(item, index)] || index === 0" :templateChanged="templateChanged"
            :length="_list.length" :options="options" @click.native="handleClick(item, index, $event)"
            @template-event="$emit('template-event', $event)"></component>
        </div>
      </template>

      <template v-if="_groupedList && _groupsList && observer" key="grouped">
        <div class="zoomout_c" key="zoomout_c" v-if="zoomOutView">
          <div v-for="[key, items] in _groupsList" class="zitem_c" @click="zoomIn(key)">
            <div class="main ellipsis">{{ key }}</div>
            <div class="sub">({{ items.length }}&nbsp;Items)</div>
          </div>
        </div>

        <div v-else v-for="[key, items] in _groupedList" class="group_c" :key="key + '_container'"
          :data-container-key="key">
          <component v-if="headerTemplate" :is="headerTemplate" :groupKey="key" :groupItems="items" @click="zoomOut"
            @template-event="$emit('template-event', $event)"></component>
          <div class="groupheader" v-else @click="zoomOut">{{ key }}</div>

          <transition-group name="list-transition" tag="div" class="group-items" :key="key" v-if="items && itemTemplate">
            <div class="item-wrapper" v-for="(item, itemIndex) in items" :style="[
              {
                backgroundColor: visibilityReference[keyGetter(item, itemIndex)] ? '' : printActive ? 'transparent' : '#cfcfcf',
              },
              _wrapperStyles,
            ]" :key="keyGetter(item, itemIndex)" :data-item-key="keyGetter(item, itemIndex)"
              :ref="el => { if (el) observer?.observe(el as HTMLDivElement) }">
              <component :is="printTemplate || itemTemplate" class="item_c print-only" v-if="printActive" :item="item"
                :index="itemIndex" :options="options"></component>

              <component :is="itemTemplate" :options="options" :key="keyGetter(item, itemIndex)" class="item_c screen"
                :item="item" :index="itemIndex" v-if="visibilityReference[keyGetter(item, itemIndex)] || itemIndex === 0"
                :templateChanged="templateChanged" @click.native="handleClick(item, itemIndex, $event)"
                @template-event="$emit('template-event', $event)"></component>
            </div>
          </transition-group>
        </div>
      </template>

      <button type="button" class="zoomout_btn" v-if="grouped && !noZoom" key="zoomout_btn" @click="toggleZoom"></button>
    </transition-group>
  </div>
</template>
  
<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
interface Props {
  list: any[] | Map<string, any[]>;
  keyGetter: (item: any, index?: number) => string;
  clickHandler?: Function;
  itemTemplate: any;
  printTemplate?: any;
  printActive?: boolean;
  headerTemplate?: any;
  noZoom?: boolean;
  view?: string;
  groupView?: string;
  options?: Record<string, any> | null;
  wrapperStyles?: Record<string, any>;
  observerRoot?: HTMLElement;
}

const props = withDefaults(defineProps<Props>(), {
  view: "list",
  groupView: "grid",
  printActive: false,
});

const zoomOutView = ref(false);
const root = ref<HTMLDivElement>();
const groupElements = ref<any[]>([]);
const visibilityReference = reactive<{ [key: string]: boolean }>({});
const observer = ref<IntersectionObserver>();
const templateChanged = ref(0);
const itemHeight = ref();
const itemWidth = ref();

function handleRootMount(rootElement: HTMLDivElement) {
  if (rootElement) {
    root.value = rootElement;
    observer.value = new IntersectionObserver(observerCallback, {
      root: props.observerRoot || rootElement,
      rootMargin: "100px",
      threshold: 0.1,
    });
  }
}

onMounted(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (root.value) {
        let item_c = root.value.querySelector(".item_c");
        if (item_c) {
          //    let bcr = item_c.getBoundingClientRect();
          itemHeight.value = (<HTMLDivElement>item_c).offsetHeight; //bcr.bottom - bcr.top;
          itemWidth.value = (<HTMLDivElement>item_c).offsetWidth; //bcr.right - bcr.left;
        }

        if (props.options && props.options.displaying) {
          let key = props.keyGetter(props.options.displaying);
          if (key) {
            let e = root.value.querySelector(".item-wrapper[data-item-key='" + key + "']");
            if (e) e.scrollIntoView();
          }
        }
      }
    });
  });
});

onBeforeUnmount(() => observer.value?.disconnect());

watch(
  () => props.itemTemplate,
  () => {
    nextTick(() => {
      if (observer.value) {
        let records = observer.value.takeRecords();
        observerCallback(records);
      }
      templateChanged.value++;
    });
  }
);

const observerCallback = function (entries: IntersectionObserverEntry[]) {
  entries.forEach((e: IntersectionObserverEntry) => {
    if (e.target) {
      let key = e.target.getAttribute("data-item-key");
      if (key) visibilityReference[key] = e.isIntersecting;
    }
  });
};

const _wrapperStyles = computed(() => {
  let styles: any = {};
  if (props.wrapperStyles) styles = JSON.parse(JSON.stringify(props.wrapperStyles));
  if (!styles.height) styles.height = itemHeight.value + "px";
  if (!styles.width && props.view === "grid") styles.width = itemWidth.value ? itemWidth.value + "px" : "100%";
  return styles;
});

const _list = computed(() => {
  if (Array.isArray(props.list)) return props.list;
});

const _groupedList = computed(() => {
  if (isOfType(props.list, "get")) return props.list;
});

const _groupsList = computed(() => {
  if (isOfType(props.list, "get")) return props.list;
});

const grouped = computed(() => {
  return isOfType(props.list, "get");
});

watch(
  () => grouped.value,
  () => {
    if (observer.value) {
      observer.value.disconnect();
      observer.value = new IntersectionObserver(observerCallback, {
        root: root.value,
      });
    }
    nextTick(() => {
      if (observer.value) {
        let records = observer.value.takeRecords();
        observerCallback(records);
      }
      templateChanged.value++;
    });
  }
);

const handleClick = function (item: any, index: number, evt: Event) {
  if (props.clickHandler) props.clickHandler(item, index, evt);
};

const zoomOut = function () {
  if (grouped.value) zoomOutView.value = true;
};

const toggleZoom = function () {
  if (grouped.value) zoomOutView.value = !zoomOutView.value;
};

const zoomIn = function (key: string) {
  zoomOutView.value = false;
  nextTick(function () {
    if (root.value) {
      if (_groupedList.value) {
        let offset = 0;
        for (const [groupKey, items] of _groupedList.value) {
          if (key !== groupKey) {
            let container = root.value.querySelector("[data-container-key='" + groupKey + "']");
            if (container?.clientHeight) offset += container.clientHeight + 40; //40 margin
          } else break;
        }
        root.value.scrollTop = offset;
      }
    }
  });
};

function encodeId(str: string) {
  return btoa(str);
}

defineExpose({ root });
</script>
  
<style scoped>
.listview {
  position: relative;
  transition: height, 0.5s;
  max-width: 100%;
}

.listview.zoomout {
  display: none;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  align-content: flex-start;
}

.listview.zoomout .zoomout_c {
  display: flex;
}

.zoomout_c {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  align-content: flex-start;
}

.group_c {
  margin: 0px 10px 40px 0px;
  order: 1;
  flex: 1;
  max-width: 100%;
}

.groupheader {
  display: inline-block;
  font-size: x-large;
  font-weight: 100;
  margin-left: 5px;
  margin-bottom: 3px;
}

.group-items {
  display: flex;
  flex-wrap: wrap;
}

.listview.grouplist .group-items {
  display: block;
}

.item-wrapper {
  margin: 5px;
  overflow: visible;
}

.zoomout_btn {
  height: 2px;
  width: 24px;
  border-top: 12px solid rgba(255, 255, 255, 0.8);
  border-right: 5px solid rgba(255, 255, 255, 0.8);
  border-bottom: 12px solid rgba(255, 255, 255, 0.8);
  border-left: 5px solid rgba(255, 255, 255, 0.8);
  background-color: black;
  position: sticky;
  bottom: 3px;
  left: 100%;
  opacity: 0.6;
}

.zoomout_btn:hover {
  border-top: 12px solid rgba(255, 255, 255, 1);
  border-right: 5px solid rgba(255, 255, 255, 1);
  border-bottom: 12px solid rgba(255, 255, 255, 1);
  border-left: 5px solid rgba(255, 255, 255, 1);
  opacity: 1;
}

.zoomout .zoomout_btn {
  display: none;
}

/*Zoomout item */
.listview.zoomout {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
}

.listview.zoomout .zitem_c {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 165px;
  height: 60px;
  margin: 5px;
  background-color: #303030;
  color: #dfdfdf;
}

.zitem_c {
  display: none;
}

.zitem_c:hover {
  background-color: #202020;
}

.zitem_c .main {
  font-size: small;
  font-weight: 600;
}

.zitem_c .sub {
  font-size: x-small;
}

.print-only {
  visibility: hidden;
}

@media screen and (max-width: 767px) {
  .zoomout_btn {
    display: none;
  }
}

@media print {
  .listview {
    height: auto;
    overflow-y: visible;
  }

  .item_c.screen {
    display: none;
  }

  .print-only {
    visibility: visible;
  }

  .item_c {
    page-break-inside: avoid;
  }

  .groupheader {
    font-size: medium;
    font-weight: 600;
    margin-left: 0px;
  }

  .listview.grouplist .group-items {
    display: flex;
    flex-wrap: wrap;
  }

  .item-wrapper {
    height: auto !important;
    width: auto !important;
    page-break-inside: avoid;
  }

  .zoomout_btn {
    display: none;
  }
}

/*transition*/

.zoomfade-enter-active {
  animation: zoomIn 0.8s;
}

.zoomfade-leave-active {
  animation: zoomOut 0s;
  display: none;
  height: 0px;
  position: absolute;
}

.zoomfade-move {
  display: none;
  position: absolute;
}

@keyframes zoomOut {
  from {
    opacity: 1;
  }

  50% {
    opacity: 0;
    /* transform: scale3d(0.3, 0.3, 0.3); */
  }

  to {
    opacity: 0;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  50% {
    opacity: 0.5;
  }

  90% {
    opacity: 1;
  }
}

.list-transition-enter,
.list-transition-leave-to {
  opacity: 0;
  transform: translateX(200px);
}

.list-transition-leave-active {
  position: absolute;
}
</style>
  